import type { PropertyValues, TemplateResult } from 'lit';
import { css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { EntityContent } from 'src/content/entity-content.js';
import { ghsLabels } from 'src/store/selectors';
import '../../content/substances/d-inline-robot-alert.js';
import '../../content/substances/d-view-document.js';
import '../../library/editors/index.js';
import '../../library/elements/d-section.js';
import '../../library/elements/d-wrap.js';
import '../../library/fields/d-view-text.js';
import '../../library/lists/d-list-section.js';
import '../../library/lists/d-list-section-attachment.js';
import '../../library/lists/d-list-section-related-risk-assessments.js';
import './d-ghs-hazard-label.js';
import { notifyError } from 'src/utilities/monitoring.js';
import { riskLevelFromRisk } from 'src/models/risk.js';
import { t } from 'src/i18n';
import { isEmptyOrInvalidString, uuid } from 'src/utilities/text';
import { displayAlert } from 'src/utilities/display-alert';
import { DNewPartnerDialog } from 'src/layout/parts/d-new-partner-dialog';
import type { UpdateSectionItem } from '../update-section-item';
import type { UploadedFile } from '../../pages/substances-page/uploaded-file';
import type { ListItemIcon } from '../../library/lists/listItemIcon';
import type { ListSectionItemInput } from '../../library/lists/listSectionItemInput';
import type { RelatedRiskAssessment } from '../../library/lists/related-risk-assessment';
import type { CreateRiskAssessmentInput } from '../../layout/parts/create-risk-assessment-input';
import type { CreateEntityInput } from '../../layout/parts/create-entity-input';
import type { ListDefinition } from '../list-definition';
import type { SubstanceViewEditItem } from './substance-view-edit-item';
import type { SubstanceView } from './substance-view';
import type { NewPartnerDialogResult } from '../../layout/parts/new-partner-dialog-result';
import type { SectionField } from '../../library/components/section-field';
import type { GestureType } from '../../library/fields/gesture-type';
import type { ActionInput } from '../../library/elements/action-input';
import type { SelectTagOption } from '../../library/editors/elements/select-tag-option';
/**
 *
 */
@customElement('d-substance-view')
export class DSubstanceView extends EntityContent<SubstanceView, SubstanceViewEditItem> {
  static readonly styles = [
    ...EntityContent.styles,
    css`
      d-view-document {
        min-height: 1000px;
      }
    `,
  ];

  @property({ type: Boolean, attribute: 'no-animation' })
  noAnimation = false;

  @state()
  private pdfLink = '';
  private pdfLinkSubstanceUuid = '';
  @query('#fileInput')
  private fileInput!: HTMLInputElement;

  private static readonly ghsIconMap: Record<string, string> = {
    GHS01: '/images/ghs/ghs-01-explosive.svg',
    GHS02: '/images/ghs/ghs-02-flammable.svg',
    GHS03: '/images/ghs/ghs-03-oxidizing.svg',
    GHS04: '/images/ghs/ghs-04-compressed-gas.svg',
    GHS05: '/images/ghs/ghs-05-corrosive.svg',
    GHS06: '/images/ghs/ghs-06-toxic.svg',
    GHS07: '/images/ghs/ghs-07-harmful.svg',
    GHS08: '/images/ghs/ghs-08-health-hazard.svg',
    GHS09: '/images/ghs/ghs-09-enviromental-hazard.svg',
  };

  private get hazardLabelOptions(): SelectTagOption[] {
    return ghsLabels().map((item) => ({
      value: item.type,
      text: item.name,
      icon: DSubstanceView.ghsIconMap[item.type],
    }));
  }

  renderViewMode() {
    return html`${this.renderFieldSection('substances', this.viewFields(), this.entityView)} `;
  }

  _suggestedRiskAssessments(hazardLabels, suggestedDocuments) {
    return hazardLabels.map(function (label) {
      return suggestedDocuments.filter(function (template) {
        return template.hazardLabel === label;
      })[0];
    });
  }

  _riskAssessmentClasses(r) {
    if (r.suggestion) {
      return 'sticker suggestion';
    }
    if (r.risk) {
      const risk = r.risk;
      if (risk > 0 && risk < 5) {
        return 'sticker risk1';
      }
      if (risk > 4 && risk < 9) {
        return 'sticker risk2';
      }
      if (risk > 8 && risk < 13) {
        return 'sticker risk3';
      }
      if (risk > 12) {
        return 'sticker risk4';
      }
    }
  }

  _getPhrases(phrases, codes) {
    let result = '';
    phrases.forEach(function (item) {
      if (codes.indexOf(item.id) > -1) {
        result += item.norwegian.replaceAll('[', '(').replaceAll(']', ')') + ' ';
      }
    });
    return result;
  }

  _supplierName(organization, item) {
    if (item.supplierUuid && organization.partnersById[item.supplierUuid]) {
      return organization.partnersById[item.supplierUuid].name;
    }
    return '';
  }

  _unreadable(item) {
    return item.uploadStatus === 'UNREADABLE';
  }

  renderPreContent() {
    const gesture: GestureType[] = ['blink', 'surprised'];
    return this.entityView.unreadable
      ? html`<d-section>
          <d-inline-robot-alert .gesture=${gesture} field="substances_unreadable"></d-inline-robot-alert>
        </d-section>`
      : html``;
  }

  asListSectionItemInputs(list: RelatedRiskAssessment[]): ListSectionItemInput[] {
    return list.map((l) => {
      let mainIcon: ListItemIcon = 'risk0';
      if (l.suggestion) {
        mainIcon = 'suggestion';
      } else if (riskLevelFromRisk(l.risk)) {
        mainIcon = ('risk' + riskLevelFromRisk(l.risk)) as ListItemIcon;
      }
      const href = l.suggestion ? undefined : this.entityView.href + '/riskAssessments/' + l.uuid;
      const clickHandler =
        l.suggestion && this.entityView.currentUserHasWriteAccess
          ? () => {
              const id = uuid();
              const input: CreateRiskAssessmentInput = {
                entityType: 'riskAssessments',
                entityUuid: id,
                pageId: 279,
                targetUrl: this.entityView.href + '/riskAssessments/' + id + '?edit',
                name: l.name,
                templateId: l.templateId,
                substanceUuid: this.entityView.uuid,
              };
              this.dispatchEvent(
                new CustomEvent<CreateEntityInput>('create-entity', {
                  bubbles: true,
                  composed: true,
                  detail: input,
                }),
              );
            }
          : undefined;
      return {
        accessible: l.suggestion
          ? this.entityView.featureStates.risk && this.entityView.currentUserHasWriteAccess
          : this.entityView.featureStates.risk,
        label: l.name,
        mainIcon,
        href,
        clickHandler,
        hasDraft: l.hasDraft,
      };
    });
  }

  onSupplierChanged(e: CustomEvent<{ value: string }>, item: SubstanceViewEditItem) {
    if (e.detail.value === 'NEW') {
      return this.addNewPartner(item);
    }
    this.editItem = { ...item, supplierUuid: e.detail.value };
  }

  renderRiskAssessments(): TemplateResult<1> {
    return html`<d-list-section-related-risk-assessments
      field="substances_riskAssessments"
      .items=${this.asListSectionItemInputs(this.entityView.relatedRiskAssessments)}
      .contentStickyTop=${this.contentStickyTop}
      .relatedItemUuid=${this.entityView.uuid}
      .relatedItemHref=${this.entityView.href}
      .disabled=${!this.entityView.featureStates.risk}
      .writeAccess=${this.entityView.currentUserHasWriteAccess}
      .employeesCount=${this.entityView.employeesCount}
      .specialTerms=${this.entityView.specialTerms}
    >
    </d-list-section-related-risk-assessments> `;
  }

  renderAfterListsContent() {
    return html`<d-view-document
        ?no-animation=${this.noAnimation}
        label="${t('substances.safetyDataSheet')}"
        .href=${this.pdfLink}
        .downloadHref=${this.pdfLink}
        .name=${this.entityView.name}
      ></d-view-document>
      <input
        accept=".pdf"
        id="fileInput"
        style="display: none;"
        @change=${(e) => this._handleFileInputChange(e)}
        type="file"
      /> `;
  }

  _handleFileInputChange(e: InputEvent) {
    e.stopPropagation();
    e.preventDefault();
    const target = e.target as HTMLInputElement;
    const files = target.files;

    this._handleFiles(files);
  }

  _handleFiles(files) {
    console.log('files', files);
    this.dispatchEvent(
      new CustomEvent<{ files: File[]; substanceId: string; callback: (_data: UploadedFile[]) => void }>(
        'update-substance-file',
        {
          composed: true,
          bubbles: true,
          detail: {
            files: files,
            substanceId: this.entityView.uuid,
            callback: (data) => {
              if (data.length === 1 && data[0].duplicate) {
                if (data[0].uuid === this.entityView.uuid) {
                  displayAlert(t('substances.noChangeAlreadyUpdated'));
                } else {
                  displayAlert(t('substances.dataSheetAlreadyRegistered'));
                }
              }
            },
          },
        },
      ),
    );
  }

  async initializeEditItem() {
    this.editItem = {
      applications: this.entityView.applications,
      archived: this.entityView.archived,
      hazardLabels: this.entityView.hazardLabels,
      location: this.entityView.location,
      notes: this.entityView.notes,
      supplierUuid: this.entityView.supplierUuid,
      name: this.entityView.name,
    };
  }

  doneDisabled(): boolean {
    return isEmptyOrInvalidString(this.editItem?.name);
  }

  renderEditItem(item: SubstanceViewEditItem): TemplateResult<1> {
    return html` <d-section>
        <d-edit-text
          field="substances_name"
          .autofocus=${!this.entityView.isConfirmedEntity}
          .selectOnFocus=${!this.entityView.isConfirmedEntity}
          placeholder="${t('substances.requiredField')}"
          mark-if-empty
          .value=${item.name}
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            this.editItem = { ...item, name: e.detail.value };
          }}
        ></d-edit-text>
      </d-section>
      <d-section>
        <d-select-tag
          field="substances_hazardLabels"
          icon-size="large"
          .options=${this.hazardLabelOptions}
          .value=${item.hazardLabels}
          @value-changed=${(e: CustomEvent<{ value: string[] }>) => {
            this.editItem = { ...item, hazardLabels: e.detail.value };
          }}
        >
        </d-select-tag>
      </d-section>
      <d-section>
        <d-edit-textarea
          field="substances_applications"
          .value=${item.applications}
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            this.editItem = { ...item, applications: e.detail.value };
          }}
        ></d-edit-textarea>
      </d-section>
      <d-section>
        <d-select-dropdown
          field="substances_supplier"
          .options="${this.entityView.partnerOptions}"
          .value=${item.supplierUuid}
          placeholder="${t('substances.selectSupplier')}"
          @value-changed=${(e) => this.onSupplierChanged(e, item)}
        >
        </d-select-dropdown>
      </d-section>
      <d-section>
        <d-edit-text
          field="substances_location"
          .value=${item.location}
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            this.editItem = { ...item, location: e.detail.value };
          }}
        ></d-edit-text>
      </d-section>
      <d-section>
        <d-edit-textarea
          field="substances_notes"
          .value=${item.notes}
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            this.editItem = { ...item, notes: e.detail.value };
          }}
        ></d-edit-textarea>
      </d-section>
      <d-section>
        <d-checkbox
          option-field="substances_archived"
          ?checked=${item.archived}
          @checked-changed=${(e: CustomEvent<{ checked: boolean }>) => {
            this.editItem = { ...item, archived: e.detail.checked };
          }}
        ></d-checkbox>
      </d-section>

      ${this.renderAfterListsContent()}`;
  }

  mainHeaderMoreActions(): ActionInput[] {
    return [
      ...super.mainHeaderMoreActions(),
      {
        name: t('substances.updateDataSheetPdf'),
        action: 'updatePdf',
      },
    ];
  }

  protected viewFields(): SectionField[] {
    return [
      {
        field: '',
        fieldForLabel: 'archived',
        type: 'view-text',
        class: 'fullWidth',
        condition: !this.entityView.unreadable && this.entityView.archived,
      },
      {
        field: 'hazardLabels',
        type: 'view-hazard-labels',
        items: this.entityView.hazardLabels,
        condition: !this.entityView.unreadable,
      },
      {
        field: 'applications',
        type: 'view-text',
        class: 'fullWidth',
        condition: !this.entityView.unreadable,
      },
      {
        field: 'supplierUuid',
        fieldForLabel: 'supplier',
        type: 'view-text',
        options: this.entityView.partnerOptions,
        class: 'fullWidth',
        condition: !this.entityView.unreadable,
      },
      {
        field: 'location',
        type: 'view-text',
        class: 'fullWidth',
        condition: !this.entityView.unreadable,
      },
      {
        field: 'notes',
        type: 'view-text',
        class: 'fullWidth',
        condition: !this.entityView.unreadable,
      },
    ];
  }

  protected lists(): ListDefinition[] {
    return [
      {
        field: '',
        type: 'custom',
        icon: '',
        items: '',
        render: () => this.renderRiskAssessments(),
      },
    ];
  }

  protected handleAction(action: string) {
    if (action === 'updatePdf') {
      this.fileInput.value = '';
      this.fileInput.click();
    }
  }

  protected hidePdf(): boolean {
    return true;
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return undefined;
  }

  protected updated(changedProperties: PropertyValues<this>) {
    super.updated(changedProperties);
    if (changedProperties.has('entityView')) {
      this.updatePdfLink();
    }
  }

  protected renderAttachments(): TemplateResult<1> {
    return html``;
  }

  private async addNewPartner(item: SubstanceViewEditItem) {
    const result: NewPartnerDialogResult = await DNewPartnerDialog.open({ partnerTerm: t('substances.supplierTerm') });
    if (result.action === 'done') {
      this.editItem = { ...item, supplierUuid: result.partnerUuid };
    }
    return result.action;
  }

  private updatePdfLink() {
    if (this.pdfLinkSubstanceUuid !== this.entityView.uuid) {
      this.pdfLinkSubstanceUuid = this.entityView.uuid;
      this.pdfLink = '';
    }
    this.entityView.pdf
      .then((p) => (this.pdfLink = p))
      .catch((e) => {
        notifyError(e);
        this.pdfLink = '';
      });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-substance-view': DSubstanceView;
  }
}
